import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChevronRight, Check, ArrowUpRight, Handshake, Mail } from "lucide-react";
import ConsentModal from "../components/ConsentModal";
import { useHoneypot } from "../components/Honeypot";
import useSeo, { buildBreadcrumb } from "../lib/useSeo";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PROJECT_TYPES = [
  "Piloto / MVP (validar idea)",
  "Software Web",
  "Aplicación móvil",
];

const INDUSTRIES = [
  "Minería",
  "Servicios",
  "Salud",
  "Educación",
  "Particular",
  "Otro (Especificar)",
];

const TIMELINES = ["< 1 mes", "1-3 meses", "3-6 meses", "6-12 meses", "+12 meses"];
const BUDGETS = ["< USD 8k", "USD 8k - 20k", "USD 20k - 50k", "USD 50k - 100k", "USD 100k - 250k", "+ USD 250k"];

export default function Cotizador() {
  useSeo({
    title: "Cotiza tu proyecto de software · DoSoft Chile",
    description: "Cuéntanos tu proyecto en 3 pasos y recibe una propuesta personalizada. Software a medida, apps móviles, MVPs, pilotos y sistemas empresariales para empresas chilenas.",
    canonical: "https://dosoft.cl/cotizador",
    jsonLd: [
      buildBreadcrumb([
        { name: "Inicio", url: "/" },
        { name: "Cotizador", url: "/cotizador" },
      ]),
    ],
  });
  const nav = useNavigate();
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentAt, setConsentAt] = useState(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [industryOther, setIndustryOther] = useState("");
  const [emailWarn, setEmailWarn] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    project_type: "", industry: "",
    timeline: "", budget_range: "", description: "",
    partnership_interest: false,
  });
  const [hp, HoneypotField] = useHoneypot();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const industryValue =
        form.industry === "Otro (Especificar)" && industryOther.trim()
          ? `Otro: ${industryOther.trim()}`
          : form.industry;
      await api.post("/quotes", {
        ...form,
        industry: industryValue,
        modules: [],
        integrations: [],
        consent_given: true,
        consent_at: consentAt,
        _hp: hp,
      });
      setDone(true);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      toast.success("Solicitud enviada correctamente");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="cotizador-success" className="min-h-[70vh] flex items-center">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-20 h-20 border-2 border-brand mx-auto flex items-center justify-center mb-8">
            <Check size={36} strokeWidth={1.5} className="text-brand" />
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl text-ash-100 tracking-tighter mb-6">
            Solicitud recibida.
          </h1>
          <p className="text-ash-300 text-lg leading-relaxed mb-8">
            Revisaremos tu solicitud y nos contactaremos contigo para coordinar los siguientes pasos.
          </p>
          <a href="/" data-testid="cotizador-back-home" className="inline-flex items-center gap-3 border border-ink-500 hover:border-brand px-8 py-4 uppercase tracking-widest text-sm text-ash-100">
            Volver al inicio <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    );
  }

  const canNext =
    (step === 1 && form.name.trim()) ||
    (step === 2 &&
      form.project_type &&
      form.industry &&
      (form.industry !== "Otro (Especificar)" || industryOther.trim()));

  const handleNext = () => {
    if (step === 1 && !EMAIL_RE.test(form.email.trim())) {
      setEmailWarn(true);
      return;
    }
    setStep(step + 1);
  };

  return (
    <div data-testid="cotizador-page" className="py-16 md:py-24">
      {!consentGiven && (
        <ConsentModal
          onAccept={() => {
            setConsentAt(new Date().toISOString());
            setConsentGiven(true);
          }}
          onReject={() => nav("/")}
        />
      )}
      {emailWarn && <EmailRequiredModal onClose={() => setEmailWarn(false)} />}
      <HoneypotField />
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-xs uppercase tracking-widest text-brand font-mono mb-4">
          Cotizador online
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-ash-100 mb-4 leading-[0.95]">
          Cotiza tu desarrollo.
        </h1>
        <p className="text-ash-300 text-lg max-w-2xl leading-relaxed mb-12">
          Cuéntanos sobre tu proyecto y nuestro equipo te contactará para conversar
          los detalles.
        </p>

        {/* Steps */}
        <div className="flex items-center gap-2 mb-12 border-b border-ink-500 pb-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2 flex-1">
              <div
                data-testid={`cotizador-step-${n}`}
                className={`w-10 h-10 border flex items-center justify-center font-mono text-sm ${
                  step >= n ? "border-brand text-brand bg-brand/5" : "border-ink-500 text-ash-500"
                }`}
              >
                {String(n).padStart(2, "0")}
              </div>
              {n < 3 && <div className={`flex-1 h-px ${step > n ? "bg-brand" : "bg-ink-500"}`} />}
            </div>
          ))}
        </div>

        <div className="border border-ink-500 bg-ink-900 p-8 md:p-12">
          {step === 1 && (
            <div data-testid="cotizador-form-step-1">
              <h2 className="font-display font-bold text-2xl text-ash-100 mb-2">Tu contacto</h2>
              <p className="text-ash-300 mb-8 text-sm">¿Cómo te ubicamos?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Nombre completo *">
                  <input data-testid="quote-input-name" className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} />
                </Field>
                <Field label="Email *">
                  <input
                    data-testid="quote-input-email"
                    type="email"
                    className={inputCls}
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                  />
                </Field>
                <Field label="Teléfono">
                  <input data-testid="quote-input-phone" className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                </Field>
                <Field label="Empresa">
                  <input data-testid="quote-input-company" className={inputCls} value={form.company} onChange={(e) => update("company", e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="cotizador-form-step-2">
              <h2 className="font-display font-bold text-2xl text-ash-100 mb-2">Tu proyecto</h2>
              <p className="text-ash-300 mb-8 text-sm">Cuéntanos qué necesitas construir.</p>

              <label className="text-xs font-semibold text-ash-300 uppercase tracking-widest mb-3 block">Tipo de proyecto *</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-8">
                {PROJECT_TYPES.map((t) => (
                  <button
                    key={t}
                    data-testid={`quote-project-${t}`}
                    type="button"
                    onClick={() => update("project_type", t)}
                    className={`text-left px-4 py-3 border transition-colors text-sm ${
                      form.project_type === t ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-100 hover:border-brand"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <label className="text-xs font-semibold text-ash-300 uppercase tracking-widest mb-3 block">Industria *</label>
              <div className="flex flex-wrap gap-2 mb-4">
                {INDUSTRIES.map((t) => (
                  <button
                    key={t}
                    data-testid={`quote-industry-${t}`}
                    type="button"
                    onClick={() => update("industry", t)}
                    className={`px-4 py-2 border transition-colors text-sm ${
                      form.industry === t ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-100 hover:border-brand"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {form.industry === "Otro (Especificar)" && (
                <input
                  data-testid="quote-industry-other"
                  placeholder="Especifica tu industria…"
                  value={industryOther}
                  onChange={(e) => setIndustryOther(e.target.value)}
                  className={inputCls}
                />
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="cotizador-form-step-3">
              <h2 className="font-display font-bold text-2xl text-ash-100 mb-2">Tiempo, presupuesto y modelo</h2>
              <p className="text-ash-300 mb-8 text-sm">Para alinear expectativas.</p>

              <label className="text-xs font-semibold text-ash-300 uppercase tracking-widest mb-3 block">Plazo deseado</label>
              <div className="flex flex-wrap gap-2 mb-8">
                {TIMELINES.map((t) => (
                  <button
                    key={t}
                    data-testid={`quote-timeline-${t}`}
                    type="button"
                    onClick={() => update("timeline", t)}
                    className={`px-4 py-2 border transition-colors text-sm ${
                      form.timeline === t ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-100 hover:border-brand"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <label className="text-xs font-semibold text-ash-300 uppercase tracking-widest mb-3 block">Rango de presupuesto</label>
              <div className="flex flex-wrap gap-2 mb-8">
                {BUDGETS.map((t) => (
                  <button
                    key={t}
                    data-testid={`quote-budget-${t}`}
                    type="button"
                    onClick={() => update("budget_range", t)}
                    className={`px-4 py-2 border transition-colors text-sm ${
                      form.budget_range === t ? "bg-brand text-ink-800 border-brand" : "border-ink-500 text-ash-100 hover:border-brand"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Partnership */}
              <button
                type="button"
                data-testid="quote-partnership-toggle"
                onClick={() => update("partnership_interest", !form.partnership_interest)}
                className={`w-full text-left p-5 border transition-colors mb-8 group ${
                  form.partnership_interest
                    ? "border-brand bg-brand/5"
                    : "border-ink-500 hover:border-brand"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-6 h-6 border flex items-center justify-center shrink-0 mt-0.5 ${
                      form.partnership_interest ? "border-brand bg-brand" : "border-ink-500"
                    }`}
                  >
                    {form.partnership_interest && <Check size={14} strokeWidth={3} className="text-ink-800" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Handshake size={18} strokeWidth={1.5} className="text-brand" />
                      <span className="font-display font-semibold text-ash-100">
                        Quiero que DoSoft sea mi socio comercial en el proyecto
                      </span>
                    </div>
                    <p className="text-sm text-ash-300 leading-relaxed">
                      Si optas por un modelo de partnership, podríamos compartir riesgos y
                      retornos — esto puede bajar significativamente el costo del desarrollo.
                    </p>
                  </div>
                </div>
              </button>

              <Field label="Descripción del proyecto">
                <textarea
                  data-testid="quote-input-description"
                  rows={5}
                  className={inputCls}
                  placeholder="Cuéntanos brevemente qué problema quieres resolver…"
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                />
              </Field>
            </div>
          )}

          {/* Nav */}
          <div className="flex items-center justify-between mt-10 pt-8 border-t border-ink-500">
            <button
              data-testid="cotizador-prev"
              type="button"
              disabled={step === 1}
              onClick={() => setStep(step - 1)}
              className="text-xs uppercase tracking-widest text-ash-300 hover:text-brand disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            {step < 3 ? (
              <button
                data-testid="cotizador-next"
                type="button"
                disabled={!canNext}
                onClick={handleNext}
                className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-6 py-3 uppercase tracking-wider text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente <ChevronRight size={16} />
              </button>
            ) : (
              <button
                data-testid="cotizador-submit"
                type="button"
                disabled={submitting}
                onClick={submit}
                className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-6 py-3 uppercase tracking-wider text-sm disabled:opacity-40"
              >
                {submitting ? "Enviando…" : "Enviar solicitud"} <ArrowUpRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-ink-800 border border-ink-500 text-ash-100 focus:border-brand focus:ring-1 focus:ring-brand outline-none px-4 py-3 text-base font-body placeholder:text-ash-500";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ash-300 uppercase tracking-widest mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function EmailRequiredModal({ onClose }) {
  return (
    <div
      data-testid="email-required-modal"
      className="fixed inset-0 z-[100] bg-ink-900/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-ink-800 border border-ink-500 max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 border border-brand flex items-center justify-center shrink-0">
            <Mail size={22} strokeWidth={1.5} className="text-brand" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-mono mb-2">
              Correo requerido
            </div>
            <h2 className="font-display font-bold text-xl text-ash-100 leading-tight">
              Necesitamos tu correo electrónico
            </h2>
          </div>
        </div>
        <p className="text-ash-300 leading-relaxed text-sm mb-8">
          Por favor incorpora un correo electrónico válido para que podamos contactarte y darte seguimiento a tu solicitud.
        </p>
        <div className="flex justify-end">
          <button
            data-testid="email-required-close"
            onClick={onClose}
            className="bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-6 py-3 uppercase tracking-wider text-xs inline-flex items-center gap-2"
          >
            Entendido <Check size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
